import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Typography,
  Chip,
  Tooltip,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  PhaseIcon as PhaseIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { get, post, put, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const DietConfigurationManagement = () => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewModal, setViewModal] = useState(false);
  const [viewingConfig, setViewingConfig] = useState(null);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    dietType: "",
    name: "",
    description: "",
    phases: [],
    phaseTransitions: [],
    settings: {
      maxWeeks: 52,
      allowPhaseOverride: false,
      requireSymptomTracking: true,
    },
    isActive: true,
  });

  // Phase form state
  const [phaseForm, setPhaseForm] = useState({
    name: "",
    displayName: "",
    duration: 1,
    description: "",
    instructions: "",
    isActive: true,
    order: 1,
  });

  // Transition form state
  const [transitionForm, setTransitionForm] = useState({
    fromPhase: "",
    toPhase: "",
    triggerWeek: 1,
    conditions: [],
    automatic: true,
    requiresAdminApproval: false,
    isActive: true,
  });

  useEffect(() => {
    fetchConfigurations();
    fetchStats();
  }, [currentPage, searchTerm]);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const response = await get(`/api/dashboard/diet-configurations?page=${currentPage}&limit=10&search=${searchTerm}`);

      if (response?.success) {
        setConfigurations(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching configurations:", error);
      toastMessage("Failed to fetch configurations", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await get("/api/dashboard/diet-configurations/stats");
      if (response?.success) {
        setStats(response.data || {});
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let response;

      if (editingConfig) {
        response = await put(`/api/dashboard/diet-configurations/${editingConfig._id}`, formData);
      } else {
        response = await post("/api/dashboard/diet-configurations", formData);
      }

      if (response?.success) {
        toastMessage(
          editingConfig 
            ? "Configuration updated successfully" 
            : "Configuration created successfully",
          "success"
        );
        setOpenModal(false);
        setEditingConfig(null);
        resetForm();
        fetchConfigurations();
      } else {
        toastMessage(response?.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      toastMessage("Failed to save configuration", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this configuration?")) {
      try {
        const response = await del(`/api/dashboard/diet-configurations/${id}`);
        if (response?.success) {
          toastMessage("Configuration deleted successfully", "success");
          fetchConfigurations();
        } else {
          toastMessage(response?.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("Error deleting configuration:", error);
        toastMessage("Failed to delete configuration", "error");
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await put(`/api/dashboard/diet-configurations/${id}/toggle`);
      if (response?.success) {
        toastMessage("Status updated successfully", "success");
        fetchConfigurations();
      } else {
        toastMessage(response?.message || "Status update failed", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toastMessage("Failed to update status", "error");
    }
  };

  const resetForm = () => {
      setFormData({
        dietType: "",
        name: "",
        description: "",
        phases: [],
      phaseTransitions: [],
      settings: {
        maxWeeks: 52,
        allowPhaseOverride: false,
        requireSymptomTracking: true,
      },
      isActive: true,
    });
    setPhaseForm({
      name: "",
      displayName: "",
      duration: 1,
      description: "",
      instructions: "",
      isActive: true,
      order: 1,
    });
    setTransitionForm({
      fromPhase: "",
      toPhase: "",
      triggerWeek: 1,
      conditions: [],
      automatic: true,
      requiresAdminApproval: false,
      isActive: true,
    });
  };

  const addPhase = () => {
    if (!phaseForm.name || !phaseForm.displayName || !phaseForm.description) {
      toastMessage("Please fill all required phase fields", "error");
      return;
    }
    
    const newPhase = { ...phaseForm, order: formData.phases.length + 1 };
    setFormData({
      ...formData,
      phases: [...formData.phases, newPhase],
    });
      setPhaseForm({
        name: "",
      displayName: "",
        duration: 1,
        description: "",
        instructions: "",
      isActive: true,
      order: 1,
      });
  };

  const removePhase = (index) => {
    const updatedPhases = formData.phases.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      phases: updatedPhases.map((phase, i) => ({ ...phase, order: i + 1 })),
    });
  };

  const addTransition = () => {
    if (!transitionForm.fromPhase || !transitionForm.toPhase || !transitionForm.triggerWeek) {
      toastMessage("Please fill all required transition fields", "error");
      return;
    }
    
    setFormData({
      ...formData,
      phaseTransitions: [...formData.phaseTransitions, { ...transitionForm }],
    });
    setTransitionForm({
      fromPhase: "",
      toPhase: "",
      triggerWeek: 1,
      conditions: [],
      automatic: true,
      requiresAdminApproval: false,
      isActive: true,
    });
  };

  const removeTransition = (index) => {
    const updatedTransitions = formData.phaseTransitions.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      phaseTransitions: updatedTransitions,
    });
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      dietType: config.dietType,
      name: config.name,
      description: config.description,
      phases: config.phases || [],
      phaseTransitions: config.phaseTransitions || [],
      settings: config.settings || {
        maxWeeks: 52,
        allowPhaseOverride: false,
        requireSymptomTracking: true,
      },
      isActive: config.isActive,
    });
    setOpenModal(true);
  };

  const openViewModal = (config) => {
    setViewingConfig(config);
    setViewModal(true);
  };

  const getDietTypeColor = (type) => {
    switch (type) {
      case "low_fodmap":
        return "primary";
      case "traditional":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "elimination":
        return "error";
      case "introduction":
        return "warning";
      case "maintenance":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">Diet Configuration Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
          >
            Add New Configuration
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Configurations
                </Typography>
                <Typography variant="h4">{stats.total || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Configurations
                </Typography>
                <Typography variant="h4">{stats.active || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Traditional Diets
                </Typography>
                <Typography variant="h4">
                  {stats.summary?.traditional?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Low FODMAP Diets
                </Typography>
                <Typography variant="h4">
                  {stats.summary?.lowFodmap?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                placeholder="Search configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
            sx={{ flexGrow: 1 }}
          />
        </Box>

        {/* Configurations Table */}
        <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Diet Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                  <TableCell>Phases</TableCell>
                <TableCell>Transitions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {configurations.map((config) => (
                    <TableRow key={config._id}>
                      <TableCell>
                        <Chip
                          label={config.dietType}
                          color={getDietTypeColor(config.dietType)}
                          size="small"
                        />
                      </TableCell>
                  <TableCell>{config.name}</TableCell>
                      <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {config.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {config.phases?.map((phase, index) => (
                            <Chip
                              key={index}
                          label={phase.name}
                              color={getPhaseColor(phase.name)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                    <Typography variant="body2">
                      {config.phaseTransitions?.length || 0} rules
                          </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={config.isActive}
                          onChange={() => handleToggleStatus(config._id)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                          onClick={() => openViewModal(config)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                          onClick={() => openEditModal(config)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(config._id)}
                          color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
              ))}
              </TableBody>
            </Table>
          </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>

        {/* Add/Edit Configuration Modal */}
        <Dialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditingConfig(null);
            resetForm();
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {editingConfig ? "Edit Diet Configuration" : "Add New Diet Configuration"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
          <DialogContent>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                <Tab label="Basic Info" />
                <Tab label="Phases" />
                <Tab label="Transitions" />
                <Tab label="Settings" />
              </Tabs>

              {/* Basic Info Tab */}
              {activeTab === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Diet Type</InputLabel>
                        <Select
                          value={formData.dietType}
                        onChange={(e) => setFormData({ ...formData, dietType: e.target.value })}
                          label="Diet Type"
                        required
                        >
                          <MenuItem value="traditional">Traditional</MenuItem>
                        <MenuItem value="low_fodmap">Low FODMAP</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        multiline
                        rows={3}
                      required
                      />
                    </Grid>
                    </Grid>
              )}

              {/* Phases Tab */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Diet Phases
                  </Typography>
                  
                  {/* Phase Form */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Phase Name"
                            value={phaseForm.name}
                          onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                          placeholder="e.g., elimination"
                        />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                          label="Display Name"
                          value={phaseForm.displayName}
                          onChange={(e) => setPhaseForm({ ...phaseForm, displayName: e.target.value })}
                          placeholder="e.g., Elimination Phase"
                          />
                        </Grid>
                      <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                          label="Duration (weeks)"
                          type="number"
                          value={phaseForm.duration}
                          onChange={(e) => setPhaseForm({ ...phaseForm, duration: parseInt(e.target.value) })}
                          inputProps={{ min: 1 }}
                          />
                        </Grid>
                      <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                          label="Order"
                          type="number"
                          value={phaseForm.order}
                          onChange={(e) => setPhaseForm({ ...phaseForm, order: parseInt(e.target.value) })}
                          inputProps={{ min: 1 }}
                          />
                        </Grid>
                      <Grid item xs={12} md={2}>
                          <Button
                          variant="contained"
                            onClick={addPhase}
                          fullWidth
                          >
                            Add Phase
                          </Button>
                        </Grid>
                      </Grid>
                    <TextField
                      fullWidth
                      label="Description"
                      value={phaseForm.description}
                      onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Instructions"
                      value={phaseForm.instructions}
                      onChange={(e) => setPhaseForm({ ...phaseForm, instructions: e.target.value })}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  </Paper>

                  {/* Existing Phases */}
                  {formData.phases.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Current Phases:
                      </Typography>
                  {formData.phases.map((phase, index) => (
                        <Paper key={index} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <Typography variant="subtitle2">
                              {phase.displayName} ({phase.name})
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Duration: {phase.duration} weeks | Order: {phase.order}
                            </Typography>
                            <Typography variant="body2">
                              {phase.description}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={() => removePhase(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Paper>
                  ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Transitions Tab */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Phase Transitions
                  </Typography>
                  
                  {/* Transition Form */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>From Phase</InputLabel>
                          <Select
                            value={transitionForm.fromPhase}
                            onChange={(e) => setTransitionForm({ ...transitionForm, fromPhase: e.target.value })}
                            label="From Phase"
                          >
                            {formData.phases.map((phase) => (
                              <MenuItem key={phase.name} value={phase.name}>
                                {phase.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>To Phase</InputLabel>
                          <Select
                            value={transitionForm.toPhase}
                            onChange={(e) => setTransitionForm({ ...transitionForm, toPhase: e.target.value })}
                            label="To Phase"
                          >
                            {formData.phases.map((phase) => (
                              <MenuItem key={phase.name} value={phase.name}>
                                {phase.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        </Grid>
                      <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                          label="Trigger Week"
                              type="number"
                          value={transitionForm.triggerWeek}
                          onChange={(e) => setTransitionForm({ ...transitionForm, triggerWeek: parseInt(e.target.value) })}
                          inputProps={{ min: 1 }}
                            />
                          </Grid>
                      <Grid item xs={12} md={2}>
                            <Button
                          variant="contained"
                          onClick={addTransition}
                          fullWidth
                        >
                          Add Transition
                            </Button>
                          </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={transitionForm.automatic}
                              onChange={(e) => setTransitionForm({ ...transitionForm, automatic: e.target.checked })}
                            />
                          }
                          label="Automatic"
                              />
                            </Grid>
                            </Grid>
                  </Paper>

                  {/* Existing Transitions */}
                  {formData.phaseTransitions.length > 0 && (
                          <Box>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Current Transitions:
                      </Typography>
                      {formData.phaseTransitions.map((transition, index) => (
                        <Paper key={index} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box>
                            <Typography variant="subtitle2">
                              {transition.fromPhase} → {transition.toPhase}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Week {transition.triggerWeek} | {transition.automatic ? "Automatic" : "Manual"}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={() => removeTransition(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Paper>
                  ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Settings Tab */}
              {activeTab === 3 && (
                    <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                      label="Max Weeks"
                      type="number"
                      value={formData.settings.maxWeeks}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, maxWeeks: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 1 }}
                        />
                      </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.settings.allowPhaseOverride}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, allowPhaseOverride: e.target.checked }
                          })}
                        />
                      }
                      label="Allow Phase Override"
                        />
                      </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.settings.requireSymptomTracking}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: { ...formData.settings, requireSymptomTracking: e.target.checked }
                          })}
                        />
                      }
                      label="Require Symptom Tracking"
                        />
                      </Grid>
                    </Grid>
                  )}
          </DialogContent>
          <DialogActions>
              <Button onClick={() => {
                setOpenModal(false);
                setEditingConfig(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? "Saving..." : (editingConfig ? "Update" : "Create")}
            </Button>
          </DialogActions>
          </form>
        </Dialog>

        {/* View Configuration Modal */}
        <Dialog
          open={viewModal}
          onClose={() => setViewModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Configuration Details</DialogTitle>
          <DialogContent>
            {viewingConfig && (
              <Box>
                    <Typography variant="h6" gutterBottom>
                  {viewingConfig.name}
                    </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  {viewingConfig.description}
                </Typography>
                
                <Typography variant="subtitle1" gutterBottom>
                  Diet Type: <Chip label={viewingConfig.dietType} color={getDietTypeColor(viewingConfig.dietType)} />
                </Typography>
                  
                  {viewingConfig.phases && viewingConfig.phases.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Phases:
                      </Typography>
                      {viewingConfig.phases.map((phase, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 1 }}>
                        <Typography variant="subtitle2">
                          {phase.displayName} ({phase.name})
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                          Duration: {phase.duration} weeks | Order: {phase.order}
                            </Typography>
                            <Typography variant="body2">
                              {phase.description}
                            </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}

                {viewingConfig.phaseTransitions && viewingConfig.phaseTransitions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Phase Transitions:
                      </Typography>
                    {viewingConfig.phaseTransitions.map((transition, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 1 }}>
                        <Typography variant="subtitle2">
                          {transition.fromPhase} → {transition.toPhase}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                          Week {transition.triggerWeek} | {transition.automatic ? "Automatic" : "Manual"}
                            </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default DietConfigurationManagement;
